import { useMemo, useState } from "react";
import { ArrowUpRight, Search, X } from "lucide-react";
import { Link } from "react-router-dom";
import Reveal from "@/components/Reveal";
import { articles, decisionIndex, transformations } from "@/data/siteData";

const filters = [["all", "All"], ["context", "Context"], ["governance", "Governance"], ["memory", "Memory"], ["learning", "Learning"]];

function articleTitle(slug) {
  return articles.find((article) => article.slug === slug)?.title || "Read the related note";
}

export default function DecisionIndex() {
  const [active, setActive] = useState("all");
  const [query, setQuery] = useState("");
  const visible = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    return decisionIndex.filter((item) => {
      if (active !== "all" && item.lens !== active) return false;
      if (!normalizedQuery) return true;
      const transformation = transformations.find((entry) => entry.id === item.transformation);
      return [item.title, item.copy, item.framework, item.insight, articleTitle(item.insight), transformation?.industry]
        .filter(Boolean)
        .some((value) => value.toLowerCase().includes(normalizedQuery));
    });
  }, [active, query]);

  const trimmedQuery = query.trim();
  const countLabel = trimmedQuery
    ? `${visible.length} ${visible.length === 1 ? "idea" : "ideas"} matching "${trimmedQuery}"`
    : `${visible.length} ${visible.length === 1 ? "idea" : "ideas"} to follow`;

  const resetSearch = () => setQuery("");

  return (
    <section className="decision-index" data-testid="decision-index">
      <div className="decision-index-head"><div><div className="section-kicker">Quiet index / 04</div><h2>Follow the questions across the work.</h2></div><p>These are the questions I keep returning to, with a note, a framework and a transformation pattern nearby when you want to go deeper.</p></div>
      <div className="decision-index-search" role="search" data-testid="decision-index-search-bar">
        <Search size={15} aria-hidden="true" />
        <input type="search" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search the questions, notes or patterns" aria-label="Search the Decision Index" data-testid="decision-index-search-input" />
        <button type="button" onClick={resetSearch} disabled={!query} aria-label="Clear Decision Index search" data-testid="decision-index-search-clear"><X size={14} /> <span>Clear</span></button>
      </div>
      <div className="decision-index-filter" data-testid="decision-index-filter-bar">{filters.map(([id, label]) => <button key={id} type="button" className={active === id ? "active" : ""} data-testid={`decision-index-filter-${id}`} onClick={() => setActive(id)}>{label}</button>)}</div>
      <div className="decision-index-meta"><span role="status" aria-live="polite" data-testid="decision-index-search-count">{countLabel}</span></div>
      <div className="decision-index-grid" data-testid="decision-index-results">
        {visible.length === 0 ? <div className="decision-index-empty" data-testid="decision-index-empty-state"><strong>I could not find that question yet.</strong><p>Try a broader term or return to the complete index.</p><button type="button" className="button-quiet" onClick={resetSearch} data-testid="decision-index-reset-search">Reset search</button></div> : visible.map((item, index) => {
          const transformation = transformations.find((entry) => entry.id === item.transformation);
          return <Reveal key={item.id} delay={index * 70}><article className="decision-index-card" data-testid={`decision-index-card-${item.id}`}><span className="card-index">0{index + 1} / question</span><h3>{item.title}</h3><p>{item.copy}</p><div className="decision-index-links"><Link to="/frameworks" data-testid={`decision-index-framework-${item.id}`}>{item.framework} <ArrowUpRight size={13} /></Link><Link to={`/insights/${item.insight}`} data-testid={`decision-index-insight-${item.id}`}>{articleTitle(item.insight)} <ArrowUpRight size={13} /></Link><Link to={`/transformations#transformation-${item.transformation}`} data-testid={`decision-index-transformation-${item.id}`}>{transformation?.industry || "Transformation example"} <ArrowUpRight size={13} /></Link></div></article></Reveal>;
        })}
      </div>
    </section>
  );
}
